// src/contexts/AuthContext.js - VERSÃO CORRIGIDA
import React, { createContext, useState, useContext, useEffect } from 'react';
import { getAuthInstance } from '../firebase/config';

// Criar contexto
const AuthContext = createContext({});

// Provedor de autenticação
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [authInstance, setAuthInstance] = useState(null);

  // Inicializar Firebase Auth
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const auth = await getAuthInstance();
        setAuthInstance(auth);
        
        if (auth) {
          console.log('✅ Firebase Auth inicializado');
          
          // Ouvir mudanças no estado de autenticação
          const { onAuthStateChanged } = await import('firebase/auth');
          
          const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
              // Usuário logado
              setUser({
                uid: firebaseUser.uid,
                phoneNumber: firebaseUser.phoneNumber,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName
              });
            } else {
              // Usuário não logado
              setUser(null);
            }
            setLoading(false);
          });

          return () => unsubscribe();
        } else {
          console.warn('⚠️ Firebase Auth não disponível (modo offline)');
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Erro ao inicializar Auth:', error);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login com telefone (envia código) - MODO DESENVOLVIMENTO
  const signInWithPhone = async (phoneNumber) => {
    console.log(`📱 [DEV] Simulando login para ${phoneNumber}`);

    // Simula o resultado de confirmação
    setConfirmationResult({
      confirm: async (code) => {
        if (code === '123456') {
          return {
            user: {
              uid: `dev-${Date.now()}`,
              phoneNumber: phoneNumber.startsWith('+') ? phoneNumber : `+55${phoneNumber.replace(/\D/g, '')}`
            }
          };
        }
        throw new Error('Código inválido para desenvolvimento');
      }
    });

    return { success: true, message: 'Código enviado (modo desenvolvimento - use 123456)' };
  };

  // Verificar código
  const verifyCode = async (code) => {
    if (!confirmationResult) {
      return { 
        success: false, 
        message: 'Solicitação de login não encontrada' 
      };
    }

    try {
      const result = await confirmationResult.confirm(code);
      const user = result.user;
      
      setUser({
        uid: user.uid,
        phoneNumber: user.phoneNumber
      });
      
      return { success: true, user };
    } catch (error) {
      console.error('❌ Erro ao verificar código:', error);
      return { 
        success: false, 
        message: getErrorMessage(error.code) 
      };
    }
  };

  // Logout
  const signOut = async () => {
    if (!authInstance) {
      return { success: false, message: 'Firebase não inicializado' };
    }

    try {
      const { signOut: firebaseSignOut } = await import('firebase/auth');
      await firebaseSignOut(authInstance);
      setUser(null);
      setConfirmationResult(null);
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao fazer logout:', error);
      return { success: false, message: error.message };
    }
  };

  // Mensagens de erro em português
  const getErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/invalid-phone-number': 'Número de telefone inválido',
      'auth/missing-verification-code': 'Código de verificação não fornecido',
      'auth/invalid-verification-code': 'Código de verificação inválido',
      'auth/code-expired': 'Código expirado. Solicite um novo.',
      'auth/too-many-requests': 'Muitas tentativas. Tente novamente mais tarde.',
      'auth/network-request-failed': 'Erro de conexão. Verifique sua internet.',
      'auth/configuration-not-found': 'Firebase não configurado corretamente',
      'default': 'Erro ao fazer login. Tente novamente.'
    };
    
    return errorMessages[errorCode] || errorMessages.default;
  };

  const value = {
    user,
    loading,
    authInstance,
    signInWithPhone,
    verifyCode,
    signOut,
    confirmationResult
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      {/* Container invisível para reCAPTCHA */}
      <div id="recaptcha-container" style={{ display: 'none' }}></div>
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};